<template>
<woot-modal :show="show" :on-close="onClose" size="large">
    <woot-modal-header :header-title="$t(titleKey) || 'Import Results'" />
    
    <div class="modal-content px-8 py-6">
      <!-- Compact Summary -->
      <div class="flex items-center justify-center gap-8 pb-6 mb-6 border-b border-slate-200 dark:border-slate-700">
        <div class="text-center">
          <p class="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
            {{ normalizedResults.created }}
          </p>
          <p class="text-xs text-slate-600 dark:text-slate-400 mt-1">
            {{ $t(createdLabelKey) || 'Created' }}
          </p>
        </div>
        
        <div class="h-8 w-px bg-slate-200 dark:bg-slate-700"></div>
        
        <div class="text-center">
          <p class="text-3xl font-bold text-blue-600 dark:text-blue-400">
            {{ normalizedResults.updated }}
          </p>
          <p class="text-xs text-slate-600 dark:text-slate-400 mt-1">
            {{ $t(updatedLabelKey) || 'Updated' }}
          </p>
        </div>
        
        <div class="h-8 w-px bg-slate-200 dark:bg-slate-700"></div>
        
        <div class="text-center">
          <p class="text-3xl font-bold text-red-600 dark:text-red-400">
            {{ normalizedResults.failed_count }}
          </p>
          <p class="text-xs text-slate-600 dark:text-slate-400 mt-1">
            {{ $t(failedLabelKey) || 'Failed' }}
          </p>
        </div>
      </div>

      <!-- Failed Items List -->
      <div v-if="failedItems.length > 0">
        <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {{ $t(failedItemsTitleKey) || 'Failed Items' }} ({{ failedItems.length }})
        </h3>
        
        <div class="space-y-3 max-h-96 overflow-y-auto pr-2">
          <div 
            v-for="(item, index) in failedItems" 
            :key="index"
            class="bg-n-gray-1 dark:bg-n-vars-black-br rounded-lg p-4 hover:shadow-md transition-all"
          >
            <p class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
              {{ truncateText(item.question, 120) }}
            </p>

            <p class="text-xs text-slate-500 dark:text-slate-400 mb-1">
              {{ $t(rowLabelKey) || 'Row' }} {{ item.row || '?' }}
            </p>
            
            <p v-if="item.errors" class="text-xs text-red-600 dark:text-red-400">
              {{ item.errors }}
            </p>
          </div>
        </div>
      </div>

      <!-- Success State -->
      <div v-else class="text-center py-12">
        <div class="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
          <fluent-icon icon="checkmark" size="32" class="text-emerald-600 dark:text-emerald-400" />
        </div>
        <p class="text-base font-medium text-slate-900 dark:text-slate-100 mb-1">
          {{ $t(successTitleKey) || 'All items imported successfully' }}
        </p>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ normalizedResults.created + normalizedResults.updated }} items processed
        </p>
      </div>
    </div>

    <div class="flex justify-end gap-3 px-8 py-4 border-t border-slate-200 dark:border-slate-700">
      <woot-button variant="smooth" color-scheme="primary" @click="onClose">
        {{ $t('GENERAL.CLOSE') || 'Close' }}
      </woot-button>
    </div>
  </woot-modal>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  show: Boolean,
  results: {
    type: Object,
    default: () => ({
      created: 0,
      updated: 0,
      failed_count: 0,
      failed: [],
    }),
  },
  titleKey: {
    type: String,
    default: 'BUSINESS_DATA.IMPORT_MODAL.RESULTS_TITLE',
  },
  createdLabelKey: {
    type: String,
    default: 'BUSINESS_DATA.IMPORT_MODAL.CREATED',
  },
  updatedLabelKey: {
    type: String,
    default: 'BUSINESS_DATA.IMPORT_MODAL.UPDATED',
  },
  failedLabelKey: {
    type: String,
    default: 'BUSINESS_DATA.IMPORT_MODAL.FAILED',
  },
  failedItemsTitleKey: {
    type: String,
    default: 'BUSINESS_DATA.IMPORT_MODAL.FAILED_ITEMS',
  },
  successTitleKey: {
    type: String,
    default: 'BUSINESS_DATA.IMPORT_MODAL.ALL_SUCCESS',
  },
  rowLabelKey: {
    type: String,
    default: 'BUSINESS_DATA.IMPORT_MODAL.ROW_LABEL',
  },
});

const emit = defineEmits(['close']);

const normalizedResults = computed(() => {
  const r = props.results || {};
  return {
    created: Number(r.created) || 0,
    updated: Number(r.updated) || 0,
    failed_count: Number(r.failed_count) || 0,
    failed: Array.isArray(r.failed) ? r.failed : [],
  };
});

const failedItems = computed(() => normalizedResults.value.failed);

const onClose = () => emit('close');

const truncateText = (text, maxLength = 120) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};
</script>

<style scoped>
.modal-content {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
</style>